import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Row, Col, Statistic, Typography, Button, Spin, Tag, Radio, InputNumber, Descriptions } from 'antd';
import { ReloadOutlined, LineChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchKeepaData, logRequestFailure, type KeepaProductData } from '../../shared/api/client';
import { analyzeCompetitorScreen, type CompetitorKeepaInput } from '../../shared/lib/competitorScreenAnalysis';
import { useAppColors } from '../../shared/theme';

interface CompetitorAnalysisProps {
  mainAsin?: string;
  competitorAsins: string[];
  ourPrice?: number;
  ourCogs?: number;
}

const CompetitorAnalysis: React.FC<CompetitorAnalysisProps> = ({
  mainAsin,
  competitorAsins,
  ourPrice = 0,
  ourCogs = 0,
}) => {
  const { t } = useTranslation();
  const c = useAppColors();
  const [loading, setLoading] = useState(false);
  const [keepaData, setKeepaData] = useState<Record<string, KeepaProductData>>({});
  const [forecastMode, setForecastMode] = useState<'auto' | 'manual'>('auto');
  const [manualForecast, setManualForecast] = useState<number>(400);
  const abortControllerRef = useRef<AbortController | null>(null);

  const allAsins = useMemo(() => {
    const asins = [...competitorAsins];
    if (mainAsin && !asins.includes(mainAsin)) {
      asins.push(mainAsin);
    }
    return asins;
  }, [mainAsin, competitorAsins]);

  const loadKeepaData = useCallback(async (signal?: AbortSignal) => {
    if (allAsins.length === 0) return;
    setLoading(true);
    try {
      const data = await fetchKeepaData(allAsins, signal);
      if (!signal?.aborted) {
        setKeepaData(data);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was cancelled, ignore
      }
      logRequestFailure('Failed to load Keepa data:', err);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [allAsins]);

  useEffect(() => {
    if (allAsins.length === 0) return;

    // Cancel previous request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    loadKeepaData(controller.signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAsins.join(',')]);

  // Формула — в shared/lib/competitorScreenAnalysis.ts (те же числа считает бэкенд);
  // здесь остаётся только фильтр данных Keepa: ASIN без ответа или с ошибкой не участвует.
  const analysis = useMemo(() => {
    if (Object.keys(keepaData).length === 0) return null;
    const items: CompetitorKeepaInput[] = [];
    for (const asin of allAsins) {
      const kd = keepaData[asin];
      if (!kd || kd.error) continue;
      items.push({
        asin,
        price: kd.buy_box_price || 0,
        monthlySales: kd.monthly_sales || 0,
        fbaFee: kd.fba_fee || 0,
        referralFeePercent: kd.referral_fee ?? null,
      });
    }
    return analyzeCompetitorScreen(items, mainAsin ?? null, ourPrice, ourCogs);
  }, [keepaData, allAsins, mainAsin, ourPrice, ourCogs]);

  const displayForecast = forecastMode === 'auto' ? (analysis?.ourForecast || 0) : manualForecast;

  if (competitorAsins.length === 0 && !mainAsin) {
    return null;
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LineChartOutlined style={{ color: c.info }} />
          <span>{t('idea_calc.competitor_analysis.title')}</span>
          {loading && <Spin size="small" />}
        </div>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => {
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;
            loadKeepaData(controller.signal);
          }}
          loading={loading}
        >
          {t('idea_calc.competitor_analysis.refresh_button')}
        </Button>
      }
      variant="outlined"
      style={{ marginTop: 16 }}
    >
      {!analysis ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Typography.Text type="secondary">
            {loading ? t('idea_calc.competitor_analysis.loading_keepa') : t('idea_calc.competitor_analysis.add_competitors')}
          </Typography.Text>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row gutter={[12, 12]}>
            <Col span={8}>
              <Card size="small" style={{ background: c.successBg }}>
                <Statistic
                  title={t('idea_calc.competitor_analysis.kpi_competitor_count')}
                  value={analysis.competitorCount}
                  valueStyle={{ color: c.success, fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: c.warningBg }}>
                <Statistic
                  title={t('idea_calc.competitor_analysis.kpi_avg_price')}
                  value={analysis.avgMarketPrice}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: c.warning, fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: c.infoBg }}>
                <Statistic
                  title={t('idea_calc.competitor_analysis.kpi_total_sales')}
                  value={analysis.totalMarketSales}
                  valueStyle={{ color: c.info, fontSize: 24 }}
                  suffix={t('idea_calc.competitor_analysis.per_month_short')}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: c.infoBg }}>
                <Statistic
                  title={t('idea_calc.competitor_analysis.kpi_our_forecast')}
                  value={displayForecast}
                  valueStyle={{ color: c.accent, fontSize: 24 }}
                  suffix={t('idea_calc.competitor_analysis.per_month_short')}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: analysis.priceAdvantage >= 0 ? c.successBg : c.errorBg }}>
                <Statistic
                  title={t('idea_calc.competitor_analysis.kpi_price_advantage')}
                  value={analysis.priceAdvantage}
                  precision={2}
                  prefix={analysis.priceAdvantage >= 0 ? '+$' : '-$'}
                  valueStyle={{ color: analysis.priceAdvantage >= 0 ? c.success : c.error, fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('idea_calc.competitor_analysis.kpi_market_position')}</Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text strong>{t(`idea_calc.competitor_analysis.position_${analysis.marketPosition}`)}</Typography.Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Forecast Settings */}
          <Card size="small" style={{ marginTop: 12, background: c.fillSubtle }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Typography.Text strong>{t('idea_calc.competitor_analysis.forecast_block_title')}</Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {t('idea_calc.competitor_analysis.forecast_auto_label')}
                  </Typography.Text>
                  <Typography.Text strong style={{ color: c.success, marginLeft: 4 }}>
                    {t('idea_calc.competitor_analysis.forecast_units_per_month', { count: analysis.ourForecast })}
                  </Typography.Text>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Radio.Group
                  size="small"
                  value={forecastMode}
                  onChange={(e) => setForecastMode(e.target.value)}
                >
                  <Radio value="auto">{t('idea_calc.competitor_analysis.forecast_mode_auto')}</Radio>
                  <Radio value="manual">{t('idea_calc.competitor_analysis.forecast_mode_manual')}</Radio>
                </Radio.Group>
                {forecastMode === 'manual' && (
                  <InputNumber
                    size="small"
                    min={1}
                    value={manualForecast}
                    onChange={(v) => setManualForecast(v || 400)}
                    style={{ width: 80 }}
                  />
                )}
              </div>
            </div>
          </Card>

          {/* Competitor Details */}
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 16, marginBottom: 8 }}>
            {t('idea_calc.competitor_analysis.details_label')}
          </Typography.Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {analysis.competitors.map((comp) => (
              <Card
                key={comp.asin}
                size="small"
                style={{
                  background: comp.isOurs ? c.infoBg : c.surface,
                  border: comp.isOurs ? `2px solid ${c.info}` : `1px solid ${c.borderSubtle}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      {comp.isOurs && <Tag color="blue">{t('idea_calc.competitor_analysis.tag_ours')}</Tag>}
                      <Typography.Text strong copyable={{ text: comp.asin }}>
                        {t('idea_calc.competitor_analysis.asin_label')} {comp.asin}
                      </Typography.Text>
                    </div>
                    <Descriptions size="small" column={4}>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_price')}>
                        <Typography.Text strong>${comp.price.toFixed(2)}</Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_monthly_sales')}>
                        <Typography.Text strong>{comp.monthlySales}</Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_fba')}>
                        ${comp.fbaFee.toFixed(2)}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_referral')}>
                        ${comp.referralFee.toFixed(2)} ({comp.referralFeePercent}%)
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_profit_starred')}>
                        <Typography.Text strong style={{ color: comp.estimatedProfit >= 0 ? c.success : c.error }}>
                          ${comp.estimatedProfit.toFixed(2)}
                        </Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_margin_starred')}>
                        <Typography.Text strong style={{ color: comp.estimatedMargin >= 15 ? c.success : comp.estimatedMargin >= 0 ? c.warning : c.error }}>
                          {comp.estimatedMargin.toFixed(1)}%
                        </Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_market_share')}>
                        <Typography.Text strong style={{ color: c.accent }}>
                          {comp.marketShare.toFixed(1)}%
                        </Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('idea_calc.competitor_analysis.row_net_revenue')}>
                        ${comp.netRevenue.toFixed(2)}
                      </Descriptions.Item>
                    </Descriptions>
                    {ourCogs > 0 && (
                      <Typography.Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                        {t('idea_calc.competitor_analysis.footnote_cogs', { cogs: ourCogs.toFixed(2) })}
                      </Typography.Text>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', minWidth: 100 }}>
                    {comp.isOurs && (
                      <Statistic
                        value={comp.profit}
                        precision={2}
                        prefix="$"
                        valueStyle={{
                          fontSize: 18,
                          color: comp.profit >= 0 ? c.success : c.error,
                        }}
                      />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

export default CompetitorAnalysis;
